// Filter model v1 — the Saved-Filter Builder program's keystone shape.
//
// CONSUMER: shared (Workers runtime + web via @shared + tests).
//
// One typed shape describes "a filter" everywhere in the OS: the list
// endpoints evaluate it in SQL, the "+ Filter" builder UI edits it,
// saved views persist it, the full-result export re-runs it, and the
// natural-language path (Ask Stew) emits it. The per-entity FIELD
// REGISTRY (field-registry.ts) declares which fields and operators are
// legal; this module only defines the shape and the operator
// vocabulary.
//
// Hard rule (mirrors shared/automations/rule-catalog.ts): this module
// is PURE and dependency-free. No React, no DB client — node-pool
// tests, the Worker runtime, and the Vite bundle all import it.
//
// DECISION: v1 is AND-only (`match: "all"`). Every canonical Jake Test
// query is a conjunction, and AND-only keeps the builder UI honest and
// the NL validation simple. The `match` key exists from day one so OR
// groups ("any") are a later extension of the model, not a rewrite.
//
// DECISION: condition values are ALWAYS strings on the wire (URL
// params, saved_views.filters_json, NL output). The field registry
// says how to interpret them (number, ISO date, enum value, boolean
// "true"/"false"). This keeps serialization trivial and pushes typed
// validation into one place (validate.ts).

export const FILTER_MODEL_VERSION = 1 as const;

/** Field value types a filterable field can declare. */
export type FilterFieldType = "enum" | "string" | "number" | "date" | "boolean";

/**
 * The operator vocabulary. Date fields get three RELATIVE operators
 * ("in the last 90 days", "more than 6 months ago", "in the next 60
 * days") because that is how advisors actually phrase time — the Jake
 * Test's NL path depends on them. Absolute date bounds reuse lte/gte
 * ("on or before/after").
 */
export type FilterOp =
  | "eq"
  | "neq"
  | "in"
  | "contains"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "between"
  | "within_last_days" // date: occurred within the last N days
  | "more_than_days_ago" // date: older than N days ago
  | "next_within_days"; // date: between today and today + N days

export interface FilterCondition {
  /** Registry field key, e.g. "age", "aum", "expected_close_date". */
  field: string;
  op: FilterOp;
  /** Operator operands as strings; arity is defined per-op below. */
  values: string[];
}

export interface FilterSet {
  version: typeof FILTER_MODEL_VERSION;
  /** v1: AND of all conditions. Reserved for a future "any". */
  match: "all";
  conditions: FilterCondition[];
}

/** Server + codec cap — mirrors MAX_FILTERS in the list routes. */
export const MAX_FILTER_CONDITIONS = 20;

/** Max operands for an `in` condition (keeps SQL bind lists sane). */
export const MAX_IN_VALUES = 20;

/** Length cap applied to every free-text operand (NL-injection guard). */
export const MAX_VALUE_LENGTH = 200;

// ─── Operator metadata ───────────────────────────────────────────────

export type FilterOpArity = "one" | "two" | "many";

export interface FilterOpMeta {
  op: FilterOp;
  arity: FilterOpArity;
  /** Field types the operator legally applies to. */
  types: FilterFieldType[];
  /**
   * Plain-English labels per field type (builder UI + NL prompt +
   * chip rendering). `default` covers types without an override.
   */
  label: Partial<Record<FilterFieldType, string>> & { default: string };
}

export const FILTER_OPS: readonly FilterOpMeta[] = [
  {
    op: "eq",
    arity: "one",
    types: ["enum", "string", "number", "date", "boolean"],
    label: { default: "is", date: "is on" },
  },
  {
    op: "neq",
    arity: "one",
    types: ["enum", "string", "number", "date"],
    label: { default: "is not" },
  },
  {
    op: "in",
    arity: "many",
    types: ["enum", "string"],
    label: { default: "is any of" },
  },
  {
    op: "contains",
    arity: "one",
    types: ["string"],
    label: { default: "contains" },
  },
  {
    op: "gt",
    arity: "one",
    types: ["number", "date"],
    label: { default: "is more than", date: "is after" },
  },
  {
    op: "gte",
    arity: "one",
    types: ["number", "date"],
    label: { default: "is at least", date: "is on or after" },
  },
  {
    op: "lt",
    arity: "one",
    types: ["number", "date"],
    label: { default: "is less than", date: "is before" },
  },
  {
    op: "lte",
    arity: "one",
    types: ["number", "date"],
    label: { default: "is at most", date: "is on or before" },
  },
  {
    op: "between",
    arity: "two",
    types: ["number", "date"],
    label: { default: "is between" },
  },
  {
    op: "within_last_days",
    arity: "one",
    types: ["date"],
    label: { default: "in the last (days)" },
  },
  {
    op: "more_than_days_ago",
    arity: "one",
    types: ["date"],
    label: { default: "more than (days) ago" },
  },
  {
    op: "next_within_days",
    arity: "one",
    types: ["date"],
    label: { default: "in the next (days)" },
  },
];

const OP_BY_ID: ReadonlyMap<string, FilterOpMeta> = new Map(
  FILTER_OPS.map((m) => [m.op, m]),
);

export function isFilterOp(s: unknown): s is FilterOp {
  return typeof s === "string" && OP_BY_ID.has(s);
}

export function opMeta(op: FilterOp): FilterOpMeta {
  // OP_BY_ID is built from FILTER_OPS, so a FilterOp always resolves.
  return OP_BY_ID.get(op) as FilterOpMeta;
}

export function opAppliesToType(op: FilterOp, type: FilterFieldType): boolean {
  return opMeta(op).types.includes(type);
}

/** Inclusive operand-count bounds for an operator. */
export function expectedValueCount(op: FilterOp): { min: number; max: number } {
  switch (opMeta(op).arity) {
    case "one":
      return { min: 1, max: 1 };
    case "two":
      return { min: 2, max: 2 };
    case "many":
      return { min: 1, max: MAX_IN_VALUES };
  }
}

/** The builder-facing label for an op on a given field type. */
export function opLabel(op: FilterOp, type: FilterFieldType): string {
  const meta = opMeta(op);
  return meta.label[type] ?? meta.label.default;
}

export function emptyFilterSet(): FilterSet {
  return { version: FILTER_MODEL_VERSION, match: "all", conditions: [] };
}

/** Structural (shape-only) guard. Registry-aware checks live in validate.ts. */
export function isFilterCondition(x: unknown): x is FilterCondition {
  if (typeof x !== "object" || x === null) return false;
  const c = x as Record<string, unknown>;
  return (
    typeof c.field === "string" &&
    c.field.length > 0 &&
    isFilterOp(c.op) &&
    Array.isArray(c.values) &&
    c.values.every((v) => typeof v === "string")
  );
}
